import { Box, styled } from '@mui/material';
import type { Node } from '@xyflow/react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useGetTriggerData } from 'ogp/components/views/management/workflows/hooks/use-get-trigger-data';
import { RenderUnsettledUI } from 'shared/components';
import { useSiteDetection } from 'shared/hooks';
import { OrBlock } from './or-block';
import { AddBlockButton } from './add-block-button';
import { FormValues } from './enroll-node.types';
import { NodeEditModeEnum } from '../../../edit-node-form.types';
import { SidebarFooter } from '../../../../sidebar-footer';

export const EnrollNodeEditor = ({
  node,
  onSave,
  onClose,
}: {
  mode: NodeEditModeEnum.EditExisting | NodeEditModeEnum.EditDraft;
  node: Node;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) => {
  const { site } = useSiteDetection();
  const enrollDataQuery = useGetTriggerData(site);
  const source = (node.data ?? {}) as Partial<FormValues>;

  const form = useForm<FormValues>({
    defaultValues: {
      orBlocks: source.orBlocks?.length
        ? source.orBlocks
        : [{ andBlocks: [{ condition: 'equal' }] }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'orBlocks',
  });

  if (enrollDataQuery.status !== 'success') {
    return <RenderUnsettledUI data={enrollDataQuery} />;
  }

  const submit = form.handleSubmit((values) => {
    onSave({ ...(node.data ?? {}), ...values });
    onClose();
  });

  return (
    <FormProvider {...form}>
      <EditorWrapper>
        <Title>Select one or multiple enroll conditions for your workflow</Title>
        <BlocksWrapper>
          {fields.map((f, index) => (
            <OrBlock
              key={f.id}
              index={index}
              canRemove={fields.length > 1}
              onRemove={() => remove(index)}
              entityOptions={enrollDataQuery.data}
            />
          ))}
          <AddBlockButton
            onClick={() =>
              append({
                andBlocks: [{ condition: 'equal', entityId: [] }],
              })
            }
            label="OR"
          />
        </BlocksWrapper>

        <SidebarFooter onClose={onClose} submit={submit} />
      </EditorWrapper>
    </FormProvider>
  );
};

const EditorWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const Title = styled(Box)({
  fontWeight: 700,
  fontSize: 18,
  textAlign: 'center',
  marginBottom: 12,
  marginTop: 12,
});

const BlocksWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flex: 1,
  overflowY: 'auto',
  padding: '12px 4px',
});
